import React, { useState } from "react";
import { AlertTriangle, ExternalLink, Download, Loader2, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const PRODUCTION_URL = "https://inspection.portside.nl";

/** True if the current page is being served from the Emergent preview environment. */
export function isPreviewEnv() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return h.endsWith(".preview.emergentagent.com");
}

export default function PreviewBanner() {
  const { user } = useAuth();
  const [state, setState] = useState({ busy: false, progress: 0, error: null, done: false });

  if (!isPreviewEnv()) return null;

  const downloadBackup = async () => {
    if (state.busy) return;
    setState({ busy: true, progress: 0, error: null, done: false });
    try {
      const r = await api.get("/admin/backup", {
        responseType: "blob",
        timeout: 300_000, // 5 min — backup can be large
        onDownloadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setState((s) => ({ ...s, progress: pct }));
          } else {
            // Total unknown — show loaded MB
            setState((s) => ({ ...s, progress: Math.round(evt.loaded / (1024 * 1024)) }));
          }
        },
      });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const now = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.download = `portside-backup-${now}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState({ busy: false, progress: 100, error: null, done: true });
      setTimeout(() => setState((s) => ({ ...s, done: false })), 4000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Backup failed";
      setState({ busy: false, progress: 0, error: msg, done: false });
      setTimeout(() => setState((s) => ({ ...s, error: null })), 6000);
    }
  };

  const renderBackupLabel = () => {
    if (state.busy) {
      const label = state.progress > 100 ? `${state.progress} MB` : `${state.progress || "…"}%`;
      return <>Building… {label}</>;
    }
    if (state.done) return <>Downloaded</>;
    return <>Backup ZIP</>;
  };

  return (
    <div
      data-testid="preview-banner"
      className="bg-[#ff5b2c] text-white px-4 py-3 border-b border-[#e54f24]"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <AlertTriangle size={20} className="flex-shrink-0" />
          <div>
            <div className="font-bold text-sm uppercase tracking-wider">
              Development preview · do not use for live data
            </div>
            <div className="text-xs opacity-90 mt-0.5">
              Anything added here is <strong>not visible to the team</strong>. Use the production app instead.
              {state.error && (
                <span className="ml-2 inline-block bg-white/15 px-2 py-0.5 rounded">{state.error}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {user?.role === "admin" && (
            <button
              type="button"
              data-testid="preview-banner-backup-btn"
              onClick={downloadBackup}
              disabled={state.busy}
              className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 disabled:bg-white/25 disabled:cursor-not-allowed px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide border border-white/30 min-w-[140px] justify-center"
              title="Download a ZIP of all data + signatures (can take 30–60s for large datasets)"
            >
              {state.busy ? (
                <Loader2 size={13} className="animate-spin" />
              ) : state.done ? (
                <Check size={13} />
              ) : (
                <Download size={13} />
              )}
              {renderBackupLabel()}
            </button>
          )}
          <a
            href={PRODUCTION_URL}
            data-testid="preview-banner-prod-link"
            className="inline-flex items-center gap-1.5 bg-white text-[#ff5b2c] px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide hover:bg-slate-100"
          >
            Go to live app <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </div>
  );
}
